/**
 * Date validation utility functions
 */
import { isValidDate, parseDate, dateRegex } from './dateUtils';

/**
 * Validate if a string is in a valid date format
 * @param dateString - The date string to validate
 * @returns Whether the date string is in a valid format
 */
export const validateDateFormat = (dateString: string): boolean => {
  if (!dateString) return true;
  return parseDate(dateString, false) !== null;
};

/**
 * Check if a date string is in a valid format but represents an invalid date
 * @param dateString - The date string to check
 * @returns Whether the date string is in a valid format but represents an invalid date
 */
export const isInvalidCalendarDate = (dateString: string): boolean => {
  // Check if it matches MM/DD/YYYY or MM/DD/YY format
  const fullYearMatch = dateString.match(dateRegex.fullYear);
  const shortYearMatch = dateString.match(dateRegex.shortYear);
  
  if (fullYearMatch) {
    const month = parseInt(fullYearMatch[1], 10);
    const day = parseInt(fullYearMatch[2], 10);
    const year = parseInt(fullYearMatch[3], 10);
    
    // Check if month and day are in valid ranges
    if (month < 1 || month > 12) return true; // This IS a calendar date issue
    if (day < 1 || day > 31) return true; // This IS a calendar date issue
    
    // Check if the date is valid
    return !isValidDate(month, day, year);
  }
  
  if (shortYearMatch) {
    const month = parseInt(shortYearMatch[1], 10);
    const day = parseInt(shortYearMatch[2], 10);
    let year = parseInt(shortYearMatch[3], 10);
    
    // Convert 2-digit year to 4-digit year
    year = year < 50 ? 2000 + year : 1900 + year;
    
    // Check if month and day are in valid ranges
    if (month < 1 || month > 12) return true; // This IS a calendar date issue
    if (day < 1 || day > 31) return true; // This IS a calendar date issue
    
    // Check if the date is valid
    return !isValidDate(month, day, year);
  }
  
  return false;
};

/**
 * Validate a date string and return an appropriate error message
 * @param value - The date string to validate
 * @param allowPartial - Whether to allow partial inputs (for backspacing)
 * @returns Error message or undefined if valid
 */
export const validateDateInput = (value: string, allowPartial = false): string | undefined => {
  if (!value) return undefined;
  
  // Check if the input contains non-numeric characters (except /)
  const hasInvalidChars = dateRegex.invalidChars.test(value);
  if (hasInvalidChars) {
    return "Date can only contain numbers and slashes";
  }
  
  // Check for invalid year lengths (more than 4 digits)
  if (dateRegex.invalidYearLength.test(value)) {
    return "Year must be 2 or 4 digits";
  }
  
  // If we're allowing partial inputs, check if it's a partial input
  if (allowPartial) {
    // Check if it's a partial input (not a complete date)
    const isPartialInput = !dateRegex.fullYear.test(value) && !dateRegex.shortYear.test(value);
    
    // Check specifically for partial years like "03/16/202"
    const isPartialYear = dateRegex.partialYear.test(value) && 
                         !dateRegex.fullYear.test(value) && 
                         !dateRegex.shortYear.test(value);
    
    if (isPartialInput || isPartialYear) {
      // Don't validate partial inputs when allowPartial is true
      // This is used during typing to allow the user to enter a date
      return undefined;
    }
  } else {
    // If we're not allowing partial inputs, check if it's a partial input
    const isPartialInput = !dateRegex.fullYear.test(value) && !dateRegex.shortYear.test(value);
    
    if (isPartialInput) {
      // Return an error for partial inputs when allowPartial is false
      // This is used when the field loses focus (blur event)
      return "Please enter a complete date in MM/DD/YYYY format";
    }
  }
  
  // First check if it matches the expected format
  const isFullYearFormat = dateRegex.fullYear.test(value);
  const isShortYearFormat = dateRegex.shortYear.test(value);
  
  if (!isFullYearFormat && !isShortYearFormat) {
    return "Please enter a valid date in MM/DD/YYYY format";
  }
  
  // Check if it's a valid date format but an invalid calendar date (like 02/30/2022)
  if (isInvalidCalendarDate(value)) {
    return "Please enter a valid calendar date";
  }
  
  // Check if it's a valid date format
  if (!validateDateFormat(value)) {
    return "Please enter a valid date in MM/DD/YYYY format";
  }
  
  return undefined;
}; 